import { useEffect, useState } from "react";
import { MobileSheet } from "../../components/MobileSheet.tsx";
import { MobileEntityRow, MobileSection } from "../../mobile/mobilePrimitives.tsx";
import { helpers } from "../../util/helpers.ts";
import { formatSeasonRecord } from "../../util/seasonRecord.ts";
import i18n from "../../util/i18n.ts";
import { createMobileReturnUrl } from "../../util/mobilePageContext.ts";
import { formatPlayerName } from "../../util/playerNames.ts";
import { translateRoundsWonText } from "../../util/roundsWonText.ts";
import { toWorker } from "../../util/toWorker.ts";

// M3-1: the team-history page became this bottom-sheet, mounted on the roster
// (team) tab. It works for the user's own team AND any scouted opponent (the
// roster page can show other teams). Data is fetched ad hoc on open via the
// `getTeamHistorySheet` worker RPC. The old page's heavy season/career/jersey
// DataTables are distilled into compact top-N lists so it stays a glance, not a
// page. See MOBILE_SHEET_CONTRACT_V1 in MobileSheet.tsx.

type TeamHistorySheetData = {
  tid: number;
  abbrev: string;
  season: number;
  totalWon: number;
  totalLost: number;
  totalTied: number;
  totalOtl: number;
  championships: number;
  playoffAppearances: number;
  finalsAppearances: number;
  lastChampionship?: number;
  bestRecord?: {
    season: number;
    won: number;
    lost: number;
    tied?: number;
    otl?: number;
    abbrev: string;
    tid: number;
  };
  recentSeasons: {
    season: number;
    won: number;
    lost: number;
    tied?: number;
    otl?: number;
    tid: number;
    abbrev: string;
    roundsWonText: string;
    champion: boolean;
    madePlayoffs: boolean;
  }[];
  retiredJerseys: {
    number: string;
    firstName?: string;
    lastName?: string;
    pid?: number;
    seasonRetired: number;
    text?: string;
  }[];
  leaderStat: string;
  leaders: {
    pid: number;
    firstName: string;
    firstNameShort?: string;
    lastName: string;
    pos?: string;
    value: number;
    numRings: number;
  }[];
  teamColors?: [string, string, string];
};

const getMobileScrollY = () => (typeof window !== "undefined" ? window.scrollY : undefined);

export const TeamHistorySheet = ({
  onClose,
  open,
  teamName,
  tid,
}: {
  onClose: () => void;
  open: boolean;
  teamName: string;
  tid: number;
}) => {
  const [data, setData] = useState<TeamHistorySheetData | undefined>(undefined);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setData(undefined);
    (async () => {
      const payload = await toWorker("main", "getTeamHistorySheet", tid);
      if (!cancelled) {
        setData(payload);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, tid]);

  const playerHref = (pid: number) =>
    createMobileReturnUrl(helpers.leagueUrl(["player", pid]), {
      pageID: "roster",
      returnLabel: i18n.t("roster.returnToMyRoster", {
        ns: "ui",
        defaultValue: "Return to my roster",
      }),
      scrollY: getMobileScrollY(),
    });

  const title = i18n.t("teamHistory.mobileHeroTitle", {
    ns: "ui",
    defaultValue: "{{team}} team history",
    team: teamName,
  });

  return (
    <MobileSheet onClose={onClose} open={open} title={title}>
      {data === undefined ? (
        <div className="d-flex justify-content-center py-4" data-team-history-sheet-loading="">
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">
              {i18n.t("common.loading", { ns: "ui", defaultValue: "Loading…" })}
            </span>
          </div>
        </div>
      ) : (
        <div data-team-history-sheet-content="">
          <p className="text-body-secondary" data-team-history-sheet-summary="">
            {i18n.t("teamHistory.mobileHeroDescription", {
              ns: "ui",
              defaultValue:
                "{{record}} all-time, {{playoffAppearances}} playoff trips, and {{finalsAppearances}} Finals trips.",
              finalsAppearances: data.finalsAppearances,
              playoffAppearances: data.playoffAppearances,
              record: formatSeasonRecord({
                won: data.totalWon,
                lost: data.totalLost,
                otl: data.totalOtl,
                tied: data.totalTied,
              }),
            })}
          </p>

          <div className="d-flex flex-wrap gap-2 mb-3" data-team-history-sheet-honors="">
            {data.championships > 0 ? (
              <span className="badge text-bg-warning">
                {i18n.t("roster.teamHistoryChampBadge", {
                  ns: "ui",
                  count: data.championships,
                  defaultValue: "{{count}} championship(s)",
                })}
              </span>
            ) : (
              <span className="badge text-bg-light border">
                {i18n.t("teamHistory.mobileNoTitles", {
                  ns: "ui",
                  defaultValue: "No titles yet",
                })}
              </span>
            )}
            {data.bestRecord ? (
              <span className="badge text-bg-light border">
                {i18n.t("roster.teamHistoryBestBadge", {
                  ns: "ui",
                  defaultValue: "Best {{season}}: {{record}}",
                  record: formatSeasonRecord({
                    won: data.bestRecord.won,
                    lost: data.bestRecord.lost,
                    otl: data.bestRecord.otl,
                    tied: data.bestRecord.tied,
                  }),
                  season: data.bestRecord.season,
                })}
              </span>
            ) : null}
          </div>

          <MobileSection
            className="mb-3"
            title={i18n.t("teamHistory.mobileRecentTimeline", {
              ns: "ui",
              defaultValue: "Recent five-year story",
            })}
          >
            {data.recentSeasons.length > 0 ? (
              <div data-team-history-sheet-seasons="">
                {data.recentSeasons.map((row) => (
                  <MobileEntityRow
                    key={row.season}
                    meta={
                      row.season === data.season
                        ? i18n.t("teamHistory.mobileSeasonInProgress", {
                            ns: "ui",
                            defaultValue: "In progress",
                          })
                        : row.champion
                          ? i18n.t("teamHistory.mobileSeasonChampion", {
                              ns: "ui",
                              defaultValue: "Champion",
                            })
                          : row.madePlayoffs
                            ? i18n.t("teamHistory.mobileSeasonPlayoffs", {
                                ns: "ui",
                                defaultValue: "Playoffs",
                              })
                            : i18n.t("teamHistory.mobileSeasonMissed", {
                                ns: "ui",
                                defaultValue: "Missed playoffs",
                              })
                    }
                    title={
                      <span className={row.champion ? "fw-bold" : undefined}>
                        {row.season}:{" "}
                        {formatSeasonRecord({
                          won: row.won,
                          lost: row.lost,
                          otl: row.otl,
                          tied: row.tied,
                        })}
                      </span>
                    }
                    trailing={
                      row.roundsWonText ? (
                        <span className="small text-body-secondary text-end">
                          {translateRoundsWonText(row.roundsWonText)}
                        </span>
                      ) : undefined
                    }
                  />
                ))}
              </div>
            ) : (
              <p className="text-body-secondary small mb-0">
                {i18n.t("teamHistory.mobileNoRecentSeasons", {
                  ns: "ui",
                  defaultValue: "No recent seasons yet.",
                })}
              </p>
            )}
          </MobileSection>

          <MobileSection
            className="mb-3"
            title={i18n.t("roster.teamHistoryLeadersTitle", {
              ns: "ui",
              defaultValue: "Franchise scoring leaders",
            })}
          >
            {data.leaders.length > 0 ? (
              <div data-team-history-sheet-leaders="">
                {data.leaders.map((p) => (
                  <MobileEntityRow
                    key={p.pid}
                    title={
                      <a href={playerHref(p.pid)}>
                        {formatPlayerName(p.firstName, p.lastName)}
                      </a>
                    }
                    trailing={
                      <span className="small text-nowrap">
                        {i18n.t("roster.teamHistoryLeaderPts", {
                          ns: "ui",
                          defaultValue: "{{value}} pts",
                          value: helpers.roundStat(p.value, data.leaderStat),
                        })}
                      </span>
                    }
                  />
                ))}
              </div>
            ) : (
              <p className="text-body-secondary small mb-0">
                {i18n.t("teamHistory.mobileNoRepresentativePlayer", {
                  ns: "ui",
                  defaultValue: "No player history yet",
                })}
              </p>
            )}
          </MobileSection>

          <MobileSection
            title={i18n.t("teamHistory.retiredJerseysShort", {
              ns: "ui",
              defaultValue: "Retired Jerseys",
            })}
          >
            {data.retiredJerseys.length > 0 ? (
              <div data-team-history-sheet-jerseys="">
                {data.retiredJerseys.map((row) => (
                  <MobileEntityRow
                    key={`${row.number}-${row.seasonRetired}`}
                    meta={i18n.t("teamHistory.mobileRetiredIn", {
                      ns: "ui",
                      defaultValue: "Retired in {{season}}",
                      season: row.seasonRetired,
                    })}
                    rank={`#${row.number}`}
                    title={
                      row.pid !== undefined ? (
                        <a href={playerHref(row.pid)}>
                          {formatPlayerName(row.firstName ?? "", row.lastName ?? "")}
                        </a>
                      ) : (
                        row.text
                      )
                    }
                  />
                ))}
              </div>
            ) : (
              <p className="text-body-secondary small mb-0">
                {i18n.t("teamHistory.mobileNoRetiredJerseys", {
                  ns: "ui",
                  defaultValue: "No retired jerseys yet.",
                })}
              </p>
            )}
          </MobileSection>
        </div>
      )}
    </MobileSheet>
  );
};
